import { useEffect, useState } from "react";

import DataTable from "components/table/DataTable";

import { getAttendances } from "services/api/attendanceApi";
import { getEmployees } from "services/api/employeeApi";

import { showError } from "utils/toast";

import { attendanceColumns } from "./columns";


const DEFAULT_PAGINATION = {
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 1,
};


const DEFAULT_FILTERS = {
    employeeId: "",
    fromDate: "",
    toDate: "",
    status: "",
};


export default function Attendance() {

    // =========================================================
    // DATA
    // =========================================================

    const [attendances, setAttendances] = useState([]);

    const [loading, setLoading] = useState(false);


    // =========================================================
    // PAGINATION
    // =========================================================

    const [page, setPage] = useState(1);

    const [pagination, setPagination] = useState(
        DEFAULT_PAGINATION
    );


    const limit = 10;


    // =========================================================
    // EMPLOYEES
    // =========================================================

    const [employees, setEmployees] = useState([]);

    const [employeesLoading, setEmployeesLoading] = useState(false);


    // =========================================================
    // FILTERS
    // =========================================================

    const [filters, setFilters] = useState(
        DEFAULT_FILTERS
    );


    // =========================================================
    // LOAD EMPLOYEES
    // =========================================================

    const loadEmployees = async () => {

        try {

            setEmployeesLoading(true);


            /*
             * Load a large enough list for the dropdown.
             *
             * If your employee API supports a larger limit,
             * this will fetch all employees available on page 1.
             */

            const response = await getEmployees(
                1,
                1000
            );


            console.log(
                "Employees Response:",
                response
            );


            const responseData =
                response?.data?.data ||
                response?.data ||
                {};


            const records =
                responseData?.employees ||
                responseData?.records ||
                responseData?.data ||
                [];


            setEmployees(
                Array.isArray(records)
                    ? records
                    : []
            );

        }
        catch (error) {

            console.error(
                "Employee load error:",
                error
            );


            setEmployees([]);


            showError(
                error?.response?.data?.message ||
                "Unable to load employees."
            );

        }
        finally {

            setEmployeesLoading(false);

        }

    };


    // =========================================================
    // LOAD ATTENDANCE
    // =========================================================

    const loadData = async () => {

        try {

            setLoading(true);


            /*
             * IMPORTANT:
             *
             * Send filters to the API.
             */

            const response = await getAttendances(
                page,
                limit,
                filters
            );


            console.log(
                "Attendance Response:",
                response
            );


            /*
             * Your API response is:
             *
             * {
             *   status: true,
             *   message: "...",
             *   data: {
             *      attendances: [],
             *      pagination: {}
             *   }
             * }
             */


            const responseData =
                response?.data?.data ||
                response?.data ||
                {};


            const records =
                responseData?.attendances ||
                responseData?.records ||
                [];


            const pageData =
                responseData?.pagination ||
                {};


            setAttendances(
                Array.isArray(records)
                    ? records
                    : []
            );


            setPagination({

                total:
                    Number(
                        pageData?.total || 0
                    ),

                page:
                    Number(
                        pageData?.page || page
                    ),

                limit:
                    Number(
                        pageData?.limit || limit
                    ),

                totalPages:
                    Number(
                        pageData?.totalPages ||
                        1
                    ),

            });

        }
        catch (error) {

            console.error(
                "Attendance load error:",
                error
            );


            setAttendances([]);


            setPagination(
                DEFAULT_PAGINATION
            );


            showError(
                error?.response?.data?.message ||
                "Unable to load attendance."
            );

        }
        finally {

            setLoading(false);

        }

    };


    // =========================================================
    // INITIAL LOAD
    // =========================================================

    useEffect(() => {

        loadEmployees();

    }, []);


    // =========================================================
    // LOAD ATTENDANCE
    // =========================================================

    useEffect(() => {

        loadData();

    }, [
        page,
        filters,
    ]);


    // =========================================================
    // FILTER CHANGE
    // =========================================================

    const handleFilterChange = (
        key,
        value
    ) => {

        setFilters(
            previous => ({

                ...previous,

                [key]: value,

            })
        );


        /*
         * Whenever filter changes,
         * always go back to page 1.
         */

        setPage(1);

    };


    // =========================================================
    // CLEAR FILTERS
    // =========================================================

    const handleClearFilters = () => {

        setFilters(
            DEFAULT_FILTERS
        );


        setPage(1);

    };


    // =========================================================
    // APPLY FILTERS
    // =========================================================

    const handleApplyFilters = () => {

        setPage(1);

        /*
         * Since filters are already controlled,
         * changing page to 1 triggers loadData().
         *
         * If already on page 1, manually reload.
         */

        if (page === 1) {

            loadData();

        }

    };


    // =========================================================
    // PAGINATION
    // =========================================================

    const handlePrevious = () => {

        if (
            page <= 1
        ) {

            return;

        }


        setPage(
            previous =>
                previous - 1
        );

    };


    const handleNext = () => {

        if (
            page >=
            pagination.totalPages
        ) {

            return;

        }


        setPage(
            previous =>
                previous + 1
        );

    };


    // =========================================================
    // RENDER
    // =========================================================

    return (

        <div className="mt-5">


            {/* =================================================
                FILTERS
            ================================================= */}

            <div
                className="
                    mb-5
                    rounded-xl
                    border
                    border-gray-200
                    bg-white
                    p-5
                "
            >

                {/* HEADER */}

                <div
                    className="
                        mb-5
                        flex
                        items-center
                        justify-between
                    "
                >

                    <div>

                        <h3
                            className="
                                text-base
                                font-bold
                                text-gray-800
                            "
                        >
                            Filters
                        </h3>


                        <p
                            className="
                                mt-1
                                text-sm
                                text-gray-500
                            "
                        >
                            Filter attendance records
                        </p>

                    </div>


                    <button
                        type="button"
                        onClick={
                            handleClearFilters
                        }
                        className="
                            text-sm
                            font-semibold
                            text-brand-500
                            hover:underline
                        "
                    >
                        Clear Filters
                    </button>

                </div>


                {/* FILTER GRID */}

                <div
                    className="
                        grid
                        grid-cols-1
                        gap-4
                        md:grid-cols-2
                        lg:grid-cols-4
                    "
                >

                    {/* =========================================
                        EMPLOYEE
                    ========================================= */}

                    <div>

                        <label
                            className="
                                mb-2
                                block
                                text-sm
                                font-semibold
                                text-gray-700
                            "
                        >
                            Employee Name
                        </label>


                        <select
                            value={
                                filters.employeeId
                            }
                            onChange={
                                event =>
                                    handleFilterChange(
                                        "employeeId",
                                        event.target.value
                                    )
                            }
                            disabled={
                                employeesLoading
                            }
                            className="
                                w-full
                                rounded-lg
                                border
                                border-gray-300
                                bg-white
                                px-3
                                py-2.5
                                text-sm
                                outline-none
                                focus:border-brand-500
                                disabled:cursor-not-allowed
                                disabled:bg-gray-100
                            "
                        >

                            <option value="">
                                All Employees
                            </option>


                            {
                                employees.map(
                                    employee => {

                                        const employeeId =
                                            employee?.id;


                                        const employeeName =
                                            employee?.name ||
                                            employee?.user?.name ||
                                            "-";


                                        const employeeCode =
                                            employee?.employeeCode ||
                                            "-";


                                        return (

                                            <option
                                                key={
                                                    String(
                                                        employeeId
                                                    )
                                                }
                                                value={
                                                    String(
                                                        employeeId
                                                    )
                                                }
                                            >

                                                {
                                                    employeeName
                                                }

                                                {" ("}

                                                {
                                                    employeeCode
                                                }

                                                {")"}

                                            </option>

                                        );

                                    }
                                )
                            }

                        </select>

                    </div>


                    {/* =========================================
                        FROM DATE
                    ========================================= */}

                    <div>

                        <label
                            className="
                                mb-2
                                block
                                text-sm
                                font-semibold
                                text-gray-700
                            "
                        >
                            From Date
                        </label>


                        <input
                            type="date"
                            value={
                                filters.fromDate
                            }
                            onChange={
                                event =>
                                    handleFilterChange(
                                        "fromDate",
                                        event.target.value
                                    )
                            }
                            className="
                                w-full
                                rounded-lg
                                border
                                border-gray-300
                                bg-white
                                px-3
                                py-2.5
                                text-sm
                                outline-none
                                focus:border-brand-500
                            "
                        />

                    </div>


                    {/* =========================================
                        TO DATE
                    ========================================= */}

                    <div>

                        <label
                            className="
                                mb-2
                                block
                                text-sm
                                font-semibold
                                text-gray-700
                            "
                        >
                            To Date
                        </label>


                        <input
                            type="date"
                            value={
                                filters.toDate
                            }
                            onChange={
                                event =>
                                    handleFilterChange(
                                        "toDate",
                                        event.target.value
                                    )
                            }
                            className="
                                w-full
                                rounded-lg
                                border
                                border-gray-300
                                bg-white
                                px-3
                                py-2.5
                                text-sm
                                outline-none
                                focus:border-brand-500
                            "
                        />

                    </div>


                    {/* =========================================
                        STATUS
                    ========================================= */}

                    <div>

                        <label
                            className="
                                mb-2
                                block
                                text-sm
                                font-semibold
                                text-gray-700
                            "
                        >
                            Status
                        </label>


                        <select
                            value={
                                filters.status
                            }
                            onChange={
                                event =>
                                    handleFilterChange(
                                        "status",
                                        event.target.value
                                    )
                            }
                            className="
                                w-full
                                rounded-lg
                                border
                                border-gray-300
                                bg-white
                                px-3
                                py-2.5
                                text-sm
                                outline-none
                                focus:border-brand-500
                            "
                        >

                            <option value="">
                                All Status
                            </option>


                            <option value="checked_in">
                                Checked In
                            </option>


                            <option value="checked_out">
                                Checked Out
                            </option>

                        </select>

                    </div>

                </div>


                {/* APPLY BUTTON */}

                <div
                    className="
                        mt-5
                        flex
                        justify-end
                    "
                >

                    <button
                        type="button"
                        onClick={
                            handleApplyFilters
                        }
                        disabled={
                            loading
                        }
                        className="
                            rounded-lg
                            bg-brand-500
                            px-5
                            py-2.5
                            text-sm
                            font-semibold
                            text-white
                            transition
                            hover:bg-brand-600
                            disabled:cursor-not-allowed
                            disabled:opacity-50
                        "
                    >

                        {
                            loading
                                ? "Loading..."
                                : "Apply Filters"
                        }

                    </button>

                </div>

            </div>


            {/* =================================================
                TABLE
            ================================================= */}

            <DataTable

                title="Attendance"

                columns={
                    attendanceColumns()
                }

                data={
                    attendances
                }

                loading={
                    loading
                }

            />


            {/* =================================================
                PAGINATION
            ================================================= */}

            <div
                className="
                    mt-5
                    flex
                    items-center
                    justify-between
                "
            >

                <p
                    className="
                        text-sm
                        text-gray-500
                    "
                >

                    Showing Page{" "}

                    {
                        pagination.page ||
                        page
                    }

                    {" "}of{" "}

                    {
                        pagination.totalPages ||
                        1
                    }

                </p>


                <div
                    className="
                        flex
                        items-center
                        gap-2
                    "
                >

                    <button
                        type="button"
                        onClick={
                            handlePrevious
                        }
                        disabled={
                            page <= 1 ||
                            loading
                        }
                        className="
                            rounded-lg
                            border
                            border-gray-200
                            bg-white
                            px-4
                            py-2
                            text-sm
                            font-medium
                            text-gray-700
                            disabled:cursor-not-allowed
                            disabled:opacity-40
                        "
                    >
                        Previous
                    </button>


                    <span
                        className="
                            min-w-[90px]
                            text-center
                            text-sm
                            font-medium
                            text-gray-700
                        "
                    >

                        Page{" "}

                        {
                            pagination.page ||
                            page
                        }

                        {" / "}

                        {
                            pagination.totalPages ||
                            1
                        }

                    </span>


                    <button
                        type="button"
                        onClick={
                            handleNext
                        }
                        disabled={
                            page >=
                            (
                                pagination.totalPages ||
                                1
                            ) ||
                            loading
                        }
                        className="
                            rounded-lg
                            border
                            border-gray-200
                            bg-white
                            px-4
                            py-2
                            text-sm
                            font-medium
                            text-gray-700
                            disabled:cursor-not-allowed
                            disabled:opacity-40
                        "
                    >
                        Next
                    </button>

                </div>

            </div>

        </div>

    );

}